import { useMemo } from "react";
import { isToday } from "date-fns";
import { useTranslation } from "react-i18next";
import Picker from "./picker";
import { getDatesInRange, formatDate, cn } from "@/lib/utils";

interface ScrollableCalendarProps {
  date: Date;
  setDate: (date: Date) => void;
  time?: boolean;
  pickerClassName?: string;
  pickerContainerClassName?: string;
  className?: string;
  overlayClassName?: string;
  "data-testid"?: string;
}

const ScrollableCalendar = ({
  date,
  setDate,
  time = true,
  pickerClassName,
  pickerContainerClassName,
  className,
  overlayClassName,
  "data-testid": testId,
}: ScrollableCalendarProps) => {
  const { t } = useTranslation();

  const dates = useMemo(() => {
    const startDate = new Date(new Date().setDate(new Date().getDate() - 180));
    const endDate = new Date(new Date().setDate(new Date().getDate() + 180));
    return getDatesInRange(startDate, endDate).map((date) => {
      return {
        value: formatDate(date, "yyyy-MM-dd"),
        label: isToday(date)
          ? t("title.today")
          : formatDate(date, "EEE MMM dd"),
      };
    });
  }, [t]);

  const hours = useMemo(() => {
    return Array.from({ length: 24 }, (_, index) => index).map((hour) => {
      return {
        value: hour.toString(),
        label: hour < 10 ? "0" + hour.toString() : hour.toString(),
      };
    });
  }, []);

  const minutes = useMemo(() => {
    return Array.from({ length: 60 }, (_, index) => index).map((minute) => {
      return {
        value: minute.toString(),
        label: (minute < 10 ? "0" : "") + minute.toString(),
      };
    });
  }, []);

  return (
    <div data-testid={testId} className={cn("flex w-full", className)}>
      <Picker
        containerClassName={pickerContainerClassName}
        className={cn(
          "round w-full max-w-fit justify-center text-nowrap",
          pickerClassName
        )}
        values={dates}
        defaultValue={formatDate(date ?? new Date(), "yyyy-MM-dd")}
        onChange={(value) => {
          const selectedDate = new Date(value);
          const newDate = new Date(
            selectedDate.setHours(date.getHours(), date.getMinutes())
          );
          if (!isNaN(newDate.getTime())) {
            setDate(newDate);
          }
        }}
        selectedHighlightClassName={cn("rounded-l-lg", {
          "rounded-r-lg": !time,
        })}
        overlayClassName={overlayClassName}
        id="date-picker"
      />

      {time && (
        <>
          <Picker
            containerClassName={pickerContainerClassName}
            className={cn("min-w-10 justify-center", pickerClassName)}
            values={hours}
            defaultValue={
              date
                ? date.getHours().toString()
                : new Date().getHours().toString()
            }
            onChange={(value) => {
              const newDate = new Date(date.setHours(Number(value)));
              if (!isNaN(newDate.getTime())) {
                setDate(newDate);
              }
            }}
            overlayClassName={overlayClassName}
            id="hour-picker"
          />

          <Picker
            containerClassName={pickerContainerClassName}
            className={cn("min-w-10 justify-center", pickerClassName)}
            values={minutes}
            defaultValue={
              date
                ? date.getMinutes().toString()
                : new Date().getMinutes().toString()
            }
            onChange={(value) => {
              const newDate = new Date(date.setMinutes(Number(value)));
              if (!isNaN(newDate.getTime())) {
                setDate(newDate);
              }
            }}
            selectedHighlightClassName="rounded-r-lg"
            overlayClassName={overlayClassName}
            id="minute-picker"
          />
        </>
      )}
    </div>
  );
};

export default ScrollableCalendar;
